import { AnimatedGaugeChart } from '@/components/ui/AnimatedGaugeChart'
import {
  MarketIndicatorBaseProps,
  formatSpread,
  spreadBarWidth,
} from './MarketIndicatorBase'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const MarketIndicatorDesktop = ({
  buyerPercentage = null,
  priceSpread = null,
  dealSpread = null,
  loading = false
}: MarketIndicatorBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          <span className="inline-flex items-center gap-2">
            Market indicator
            <InfoTooltip content="A snapshot of buy vs sell interest and spreads on the Secondaries order book." ariaLabel="Market indicator info" />
          </span>
        </p>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading market data...
          </div>
        </div>
      </div>
    )
  }

  const priceSpreadWidth = spreadBarWidth(priceSpread)
  const dealSpreadWidth = spreadBarWidth(dealSpread)

  return (
    <div className="h-full relative w-full">
      <p className="ml-[23px] mt-[23px] font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
        <span className="inline-flex items-center gap-2">
          Market indicator
          <InfoTooltip content="A snapshot of buy vs sell interest and spreads on the Secondaries order book." ariaLabel="Market indicator info" />
        </span>
      </p>

      <div className="absolute top-[84px] left-6 w-[340px] h-[340px]">
        <AnimatedGaugeChart
          value={buyerPercentage ?? 0}
          max={100}
          min={0}
          gaugePrimaryColor="#e3fd5a"
          gaugeSecondaryColor="#ffffff"
          className="w-full h-full"
        />
      </div>

      <p className="absolute top-[270px] left-6 font-plus-jakarta font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-sm">
        Buyer
      </p>

      <p className="absolute top-[270px] left-[204px] font-plus-jakarta font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-sm">
        Seller
      </p>

      <p className="absolute top-[100px] left-[290px] font-plus-jakarta font-normal leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base w-[153px] whitespace-pre-wrap">
        Market sentiment indicator for buyers/sellers ratio
      </p>

      <div className="absolute top-[68px] right-6 w-[395px] flex flex-col gap-2">
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-2 p-3">
          <p className="font-plus-jakarta font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] text-sm">
            Price Spread
          </p>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p className="font-plus-jakarta font-medium leading-6 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                <span className="text-[18px]">{formatSpread(priceSpread)} </span>
                <span className="text-[12px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-hidden relative w-[172px]">
                <div
                  className="absolute h-5 right-0 top-1 w-[177px] bg-transparent"
                >
                  <div
                    className="h-full w-full dark:hidden"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                    }}
                  />
                  <div
                    className="hidden h-full w-full dark:block"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(253,253,253,0.32) 1px, rgba(253,253,253,0.32) 2px)'
                    }}
                  />
                </div>
                <div
                  className="absolute bg-[#e3fd5a] h-5 left-0 top-1"
                  style={{ width: `${priceSpreadWidth}%` }}
                />
              </div>
            </div>
            <p className="font-plus-jakarta font-normal leading-3 text-[11px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap">
              Difference between buy and sell prices
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-2 p-3">
          <p className="font-plus-jakarta font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] text-sm">
            Deal Size-Spread
          </p>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p className="font-plus-jakarta font-medium leading-6 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                <span className="text-[18px]">{formatSpread(dealSpread)} </span>
                <span className="text-[12px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-hidden relative w-[172px]">
                <div
                  className="absolute h-5 right-0 top-1 w-[177px] bg-transparent"
                >
                  <div
                    className="h-full w-full dark:hidden"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                    }}
                  />
                  <div
                    className="hidden h-full w-full dark:block"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(253,253,253,0.32) 1px, rgba(253,253,253,0.32) 2px)'
                    }}
                  />
                </div>
                <div
                  className="absolute bg-[#e3fd5a] h-5 left-0 top-1"
                  style={{ width: `${dealSpreadWidth}%` }}
                />
              </div>
            </div>
            <p className="font-plus-jakarta font-normal leading-3 text-[11px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap">
              Difference between buy and sell volume
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
